"use client";

import {
  NavigationMenu,
  NavigationMenuList,
} from "@/components/ui/NavigationMenu";
import { useNavigationMenu } from "@/contexts/NavigationContext";
import { Link, usePathname } from "@/i18n/routing";
import {
  GoogleOneTap,
  SignedIn,
  SignedOut,
  SignInButton,
  useClerk,
  useUser,
} from "@clerk/nextjs";
import clsx from "clsx";
import { ChevronDown, ChevronUp, Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { useWindowSize } from "usehooks-ts";
import { Button } from "../ui/Button";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/Drawer";
import { H5, P } from "../ui/Heading";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/Popover";
import { Separator } from "../ui/Separator";
import { LanguageSelect } from "./LanguageSelect";

interface NavigationLink {
  key: string;
  href?: string;
  items?: Array<NavigationLink>;
}

const LINKS: Array<NavigationLink> = [
  {
    key: "buy",
    href: "/listing/properties-for-sale",
  },
  {
    key: "rent",
    href: "/listing/properties-for-rent",
  },
  {
    key: "commercial",
    href: "/listing/commercial-properties-for-sale",
  },
  {
    key: "off-plan",
    href: "/listing/off-plan-properties-for-sale",
  },
  {
    key: "tools",
    items: [
      {
        key: "tools-affordability-calculator",
        href: "/affordability-calculator",
      },
      {
        key: "tools-mortgage-calculator",
        href: "/mortgage-calculator",
      },
      {
        key: "tools-renovation-calculator",
        href: "/renovation-calculator",
      },
      {
        key: "tools-property-valuation-calculator",
        href: "/yalla-value",
      },
    ],
  },
  {
    key: "blogs",
    href: "/blogs",
  },
];

const LOGGED_IN_LINKS = [
  {
    key: "favorites",
    href: "/favorites",
  },
  {
    key: "search-alerts",
    href: "/search-alerts",
  },
];

const NavigationUserPopover = () => {
  const t = useTranslations("shared.navigation.header.links");

  const { width } = useWindowSize();
  const { user, isLoaded, isSignedIn } = useUser();
  const { signOut, redirectToUserProfile } = useClerk();

  if (!isLoaded || !isSignedIn) {
    return <></>;
  }

  const Content = () => {
    return (
      <div className="space-y-3 p-4 sm:p-0">
        <div className="flex gap-2 items-center">
          <div className="relative size-12">
            <Image
              src={user.imageUrl}
              fill
              alt={user.fullName!}
              className="rounded-full object-cover"
            />
          </div>
          <div>
            {user.fullName && <H5>{user.fullName}</H5>}
            <P className="text-sm">{user.primaryEmailAddress?.emailAddress}</P>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-2">
          {LOGGED_IN_LINKS.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              className="hover:text-gray-950 transition-colors text-gray-800"
            >
              {t(link.key)}
            </Link>
          ))}

          <div
            className="hover:text-gray-950 transition-colors text-gray-800 cursor-pointer"
            onClick={() => redirectToUserProfile()}
          >
            {t("manage-account")}
          </div>

          <Separator />
          <div
            className="hover:text-red-950 transition-colors text-red-800 cursor-pointer"
            onClick={async () => await signOut({ redirectUrl: "/" })}
          >
            {t("sign-out")}
          </div>
        </div>
      </div>
    );
  };

  if (width < 768) {
    return (
      <Drawer>
        <DrawerTrigger>
          <div className="relative size-9">
            <Image
              src={user.imageUrl}
              fill
              alt={user.fullName!}
              className="rounded-full object-cover"
            />
          </div>
        </DrawerTrigger>
        <DrawerContent className="space-y-3">
          <Content />
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Popover>
      <PopoverTrigger>
        <div className="relative size-9">
          <Image
            src={user.imageUrl}
            fill
            alt={user.fullName!}
            className="rounded-full object-cover"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <Content />
      </PopoverContent>
    </Popover>
  );
};

export const NavigationBar = () => {
  const t = useTranslations("shared.navigation.header.links");
  const pathname = usePathname();

  const { isMenuOpen, setMenuOpen } = useNavigationMenu();
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  const handleMouseEnter = (key: string) => {
    setOpenPopover(key);
  };

  const handleMouseLeave = () => {
    setOpenPopover(null);
  };

  const renderLink = (link: NavigationLink) => {
    if (link.items) {
      return (
        <div
          key={link.key}
          className="relative"
          onMouseEnter={() => handleMouseEnter(link.key)}
          onMouseLeave={handleMouseLeave}
        >
          <Popover open={openPopover === link.key}>
            <PopoverTrigger asChild className="!border-none !outline-none">
              <button className="px-3 py-2 gap-2 text-sm hover:text-primary transition-colors text-gray-800 cursor-pointer inline-flex items-center">
                {t(link.key)}

                {openPopover !== link.key ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronUp className="size-4" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-56 p-0 bg-background rounded-md shadow-md"
              sideOffset={-2}
            >
              <div className="flex flex-col py-2">
                {link.items.map((item) => (
                  <Link
                    key={item.key}
                    href={item.href ?? "#"}
                    className="px-4 py-2 text-sm hover:bg-accent/50 hover:text-primary transition-colors text-gray-800"
                  >
                    {t(item.key)}
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    return (
      <Link
        key={link.key}
        href={link.href ?? "#"}
        className="px-3 py-2 text-sm hover:text-primary transition-colors text-gray-800"
      >
        {t(link.key)}
      </Link>
    );
  };

  return (
    <nav
      className={clsx("px-4 py-2 bg-background", {
        "z-50 sticky top-0": isMenuOpen || !pathname.startsWith("/listing"),
      })}
      id="navbar"
    >
      <div className="flex justify-between items-center w-full max-w-screen-xl m-auto">
        {/* Logo */}
        <div className="relative h-5 w-44">
          <Link href="/">
            <Image
              src="a242c3e6-dd5a-4029-9d8e-cecaddfc9c00"
              alt="Sakani Homes"
              fill
              priority
              className="object-contain object-left"
              fetchPriority="high"
              sizes="10vw"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <NavigationMenu>
            <NavigationMenuList className="gap-2">
              {LINKS.map(renderLink)}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Desktop Login and Language */}
        <div className="hidden md:flex items-center gap-1">
          <SignedOut>
            <SignInButton>
              <Button variant={"ghost"}>{t("sign-in")}</Button>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            <NavigationUserPopover />
          </SignedIn>
          <GoogleOneTap />
          <LanguageSelect />
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-background border-b md:hidden">
          <div
            className="flex flex-col p-4 space-y-4"
            style={{ zIndex: 1000000 }}
          >
            {LINKS.map((link) => (
              <div key={link.key}>
                {link.items ? (
                  <div className="space-y-2">
                    <div className="font-medium">{t(link.key)}</div>
                    <div className="pl-4 space-y-2">
                      {link.items.map((item) => (
                        <Link
                          key={item.key}
                          href={item.href ?? "#"}
                          className="block text-sm text-gray-800 hover:text-primary"
                          onClick={() => setMenuOpen(false)}
                        >
                          {t(item.key)}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    href={link.href!}
                    className="block text-sm hover:text-primary text-gray-800"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t(link.key)}
                  </Link>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between pt-4 border-t">
              <SignedOut>
                <SignInButton>
                  <Button variant={"ghost"} className="!text-sm !p-0">
                    {t("sign-in")}
                  </Button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <NavigationUserPopover />
              </SignedIn>
              <GoogleOneTap />
              <LanguageSelect />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;
